import { useState, useEffect } from "react";
import { Outlet, useLocation, Link, useNavigate } from "react-router-dom";
import { LogOut, Plus, Sun, Moon, Lightbulb, Users, Brain } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { useTheme } from "next-themes";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { SearchBar } from "@/components/SearchBar";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/sonner";
import { CategoryList } from "@/components/Categories/CategoryList";
import { FirstTimeOnboarding } from "@/components/Onboarding/FirstTimeOnboarding";
import { useSecureAdmin } from "@/hooks/useSecureAdmin";
import {
  SidebarProvider,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarTrigger,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from "@/components/ui/sidebar";

const DashboardLayout = () => {
  const { signOut, user } = useAuth();
  const { theme, setTheme } = useTheme();
  const { isAdmin } = useSecureAdmin();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Check if user needs onboarding
  useEffect(() => {
    if (user) {
      // Check if the user has completed onboarding
      const checkOnboardingStatus = async () => {
        // First check local storage
        const onboardingCompleted = localStorage.getItem(`onboarding-completed-${user.id}`);
        
        if (!onboardingCompleted) {
          // If not in local storage, check on profile
          try {
            const { data, error } = await supabase
              .from('profiles')
              .select('onboarding_completed')
              .eq('id', user.id)
              .single();
            
            if (error) {
              console.error("Error checking onboarding status:", error);
              return;
            }
            
            if (!data?.onboarding_completed) {
              setShowOnboarding(true);
            } else {
              // Save to local storage for future reference
              localStorage.setItem(`onboarding-completed-${user.id}`, 'true');
            }
          } catch (error) {
            console.error("Error in checkOnboardingStatus:", error);
          }
        }
      };
      
      checkOnboardingStatus();
    }
  }, [user]);

  const toggleTheme = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  const handleOnboardingComplete = async () => {
    setShowOnboarding(false);
    
    // Save to local storage
    if (user) {
      localStorage.setItem(`onboarding-completed-${user.id}`, 'true');
      
      // Update the user's profile
      try {
        const { error } = await supabase
          .from('profiles')
          .update({ onboarding_completed: true })
          .eq('id', user.id);
          
        if (error) {
          console.error("Error updating onboarding status:", error);
        }
      } catch (error) {
        console.error("Error in handleOnboardingComplete:", error);
      }
    }
  };

  return (
    <>
      {/* First Time Onboarding */}
      {showOnboarding && user && (
        <FirstTimeOnboarding onComplete={handleOnboardingComplete} onSkip={handleOnboardingComplete} />
      )}

      <SidebarProvider>
        <div className="flex h-screen overflow-hidden w-full">
          <Sidebar>
            <SidebarHeader>
              <div className="flex items-center mb-6">
                <Link to="/dashboard" className="text-2xl font-bold flex-1 flex items-center gap-2">
                  Brain
                  <span className="text-2xl" role="img" aria-label="Brain logo">🧠</span>
                </Link>
              </div>
              <div className="mb-4">
                <SearchBar />
              </div>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <Link to="/dashboard/entry/new" className="flex items-center gap-2">
                      <Plus className="h-4 w-4" />
                      <span>New</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <Link to="/shared-notes" className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      <span>Shared</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                {/* Always visible Postscript link */}
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <Link to="/postscript" className="flex items-center gap-2">
                      <Lightbulb className="h-4 w-4" />
                      <span>Postscript</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarHeader>
            
            <SidebarContent>
              {/* Categories Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between px-2">
                  <h3 className="text-sm font-medium">Categories</h3>
                </div>
                <CategoryList />
              </div>
            </SidebarContent>

            <SidebarFooter>
              <div className="space-y-2">
                <div className="flex items-center gap-2 p-2">
                  <div className="flex-1 text-sm truncate">
                    {user?.email}
                  </div>
                  <Button variant="ghost" size="icon" onClick={toggleTheme}>
                    {theme === "light" ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={signOut}>
                    <LogOut className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </SidebarFooter>
          </Sidebar>

          {/* Main content */}
          <div className="flex-1 overflow-auto">
            <div className="flex items-center px-4 py-2 h-16 border-b">
              <SidebarTrigger />
              <div className="ml-4 text-xl font-bold">Brain</div>
            </div>
            <main className="p-4 lg:p-6">
              <Outlet />
            </main>
          </div>
        </div>
      </SidebarProvider>
    </>
  );
};

export default DashboardLayout;
